import { Link } from 'react-router-dom'
import { AtSign, CheckCircle2, Send, Sparkles } from 'lucide-react'
import { useTranslation } from '@/hooks/useTranslation'
import { PlatformIcon } from '@/components/brand-mentions/PlatformIcon'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
	BRAND_MENTION_PLATFORMS,
	PLATFORM_LABELS,
	type BrandMentionPlatform,
} from '@/types/brandMentions'

type PreviewMention = {
	platform: BrandMentionPlatform
	author: string
	text: string
	title?: string
	kind?: 'Comment'
	time: string
}

function previewMentions(isFrench: boolean): PreviewMention[] {
	if (isFrench) {
		return [
			{
				platform: 'x',
				author: 'Alex Martin',
				text: 'On a testé cet outil de feedback, le widget est vraiment fluide et les retours arrivent au bon endroit.',
				time: '2h',
			},
			{
				platform: 'reddit',
				author: 'u/saasfounders',
				title: 'Quelle stack pour collecter le feedback in-app ?',
				text: 'On cherche quelque chose de simple à installer. Le widget est propre et les mentions de marque aident à suivre les discussions.',
				kind: 'Comment',
				time: '5h',
			},
			{
				platform: 'linkedin',
				author: 'Camille Durand',
				text: 'On entend de plus en plus parler de cette solution. L’onboarding est clair et l’équipe produit répond vite.',
				time: '1j',
			},
			{
				platform: 'facebook',
				author: 'Growth Lab',
				text: 'Quelqu’un a déjà utilisé leur widget de recueil d’avis ? Les premiers retours ont l’air très positifs.',
				time: '2j',
			},
			{
				platform: 'youtube',
				author: 'Indie Build',
				title: 'Comment on suit ce que les gens disent du produit',
				text: 'La veille des mentions centralise X, Reddit et le reste. Pratique pour répondre sans tout surveiller à la main.',
				kind: 'Comment',
				time: '3j',
			},
		]
	}

	return [
		{
			platform: 'x',
			author: 'Alex Martin',
			text: 'Tried this feedback tool today. The widget feels smooth and the responses actually land in one place.',
			time: '2h',
		},
		{
			platform: 'reddit',
			author: 'u/saasfounders',
			title: 'What are you using for in-app feedback?',
			text: 'Looking for something easy to install. The widget looks clean and brand mentions help catch conversations we were missing.',
			kind: 'Comment',
			time: '5h',
		},
		{
			platform: 'linkedin',
			author: 'Camille Durand',
			text: 'Hearing more teams talk about this. Onboarding is clear and the product team replies fast.',
			time: '1d',
		},
		{
			platform: 'facebook',
			author: 'Growth Lab',
			text: 'Has anyone used their review widget yet? Early comments look really positive.',
			time: '2d',
		},
		{
			platform: 'youtube',
			author: 'Indie Build',
			title: 'How we keep up with what people say about the product',
			text: 'Brand mentions pulls X, Reddit and the rest into one inbox. Much easier than checking every network by hand.',
			kind: 'Comment',
			time: '3d',
		},
	]
}

function BlurredText({
	children,
	className,
}: {
	children: string
	className?: string
}) {
	return (
		<span
			className={cn('blur-[6px] select-none', className)}
			aria-hidden="true">
			{children}
		</span>
	)
}

export function BrandMentionsUpgradePreview({
	mode = 'plan',
}: {
	mode?: 'plan' | 'addon'
}) {
	const { t, i18n } = useTranslation()
	const isFrench = Boolean(i18n.language?.startsWith('fr'))
	const mentions = previewMentions(isFrench)
	const selected = mentions[0]
	const benefits = [
		t('brandMentions.lockedBenefitScan'),
		t('brandMentions.lockedBenefitNetworks'),
		t('brandMentions.lockedBenefitInbox'),
	]
	const isAddon = mode === 'addon'

	return (
		<div className="flex h-full min-h-0 flex-col overflow-hidden">
			<div
				className="relative min-h-0 flex-1 overflow-hidden"
				aria-hidden="true">
				<div className="flex h-full pointer-events-none select-none">
					<aside className="flex h-full w-full flex-col border-r border-border/60 bg-background md:w-[340px] md:min-w-[340px] lg:w-[380px] lg:min-w-[380px]">
						<div className="border-b border-border/60 bg-card/30 px-4 py-4">
							<div className="mb-3 flex items-center gap-2">
								<AtSign className="h-5 w-5 shrink-0 text-primary" />
								<p className="truncate text-xl font-semibold tracking-tight md:text-2xl">
									{t('brandMentions.title')}
								</p>
							</div>
							<div className="flex flex-wrap gap-2">
								{BRAND_MENTION_PLATFORMS.map((platform) => (
									<span
										key={platform}
										className="inline-flex items-center gap-1.5 rounded-md bg-muted px-2.5 py-1.5 text-sm font-medium text-muted-foreground">
										<PlatformIcon
											platform={platform}
											className="h-3.5 w-3.5"
											colored
										/>
										{PLATFORM_LABELS[platform]}
									</span>
								))}
							</div>
						</div>
						<div className="min-h-0 flex-1 overflow-hidden">
							<div className="space-y-2 px-2 py-2">
								{mentions.map((mention, index) => (
									<div
										key={`${mention.platform}-${index}`}
										className={cn(
											'w-full rounded-xl border p-4 text-left',
											'bg-background border-border',
											index === 0 &&
												'bg-primary/10 border-primary/50 shadow-sm ring-1 ring-primary/30'
										)}>
										<div className="flex items-start gap-3">
											<div
												className={cn(
													'flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-muted ring-2 ring-transparent',
													index === 0 && 'ring-primary/30'
												)}>
												<PlatformIcon
													platform={mention.platform}
													className="h-5 w-5"
													colored
												/>
											</div>
											<div className="min-w-0 flex-1">
												<div className="mb-1 flex items-center justify-between gap-2">
													<span className="min-w-0 truncate text-sm font-medium text-foreground">
														<BlurredText>{mention.author}</BlurredText>
													</span>
													<div className="flex shrink-0 items-center gap-1.5">
														{mention.kind === 'Comment' && (
															<span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
																{t('brandMentions.commentKind')}
															</span>
														)}
														<span className="text-xs font-medium text-muted-foreground">
															{mention.time}
														</span>
													</div>
												</div>
												<p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
													<BlurredText>{mention.title || mention.text}</BlurredText>
												</p>
											</div>
										</div>
									</div>
								))}
							</div>
						</div>
					</aside>
					<main className="hidden min-w-0 flex-1 flex-col overflow-hidden bg-muted/20 md:flex">
						<div className="flex h-full flex-col overflow-hidden bg-background">
							<header className="flex-shrink-0 border-b bg-background/95 px-3 py-3 sm:px-4">
								<div className="flex items-center gap-3">
									<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
										<PlatformIcon
											platform={selected.platform}
											className="h-5 w-5"
											colored
										/>
									</div>
									<div className="min-w-0 flex-1">
										<div className="flex items-center gap-2">
											<p className="truncate text-base font-semibold">
												<BlurredText>{selected.author}</BlurredText>
											</p>
										</div>
										<div className="flex items-center gap-2 text-xs text-muted-foreground">
											<span>{PLATFORM_LABELS[selected.platform]}</span>
											<span>•</span>
											<span>{selected.time}</span>
										</div>
									</div>
								</div>
							</header>
							<div className="flex-1 overflow-hidden p-4 sm:p-6">
								<article className="mx-auto max-w-3xl space-y-3">
									<p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">
										<BlurredText>{selected.text}</BlurredText>
									</p>
									<p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">
										<BlurredText>
											{mentions[1].text}
										</BlurredText>
									</p>
								</article>
							</div>
							<footer className="flex-shrink-0 border-t bg-background/95 p-3 sm:p-4">
								<div className="flex items-center gap-3 rounded-xl border-2 border-border/40 bg-muted/30 px-4 py-3">
									<span className="min-w-0 flex-1 text-sm text-muted-foreground">
										{t('brandMentions.replyPlaceholder', {
											platform: PLATFORM_LABELS[selected.platform],
										})}
									</span>
									<Button size="icon" className="h-9 w-9 shrink-0 shadow-md" tabIndex={-1}>
										<Send className="h-4 w-4" />
									</Button>
								</div>
							</footer>
						</div>
					</main>
				</div>
				<div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background to-transparent md:h-16" />
			</div>

			<section
				className="relative z-10 shrink-0 border-t bg-background px-4 py-5 sm:px-6 sm:py-6"
				aria-label={
					isAddon
						? t('brandMentions.addonRequired')
						: t('brandMentions.upgradeRequired')
				}>
				<div className="mx-auto w-full max-w-2xl space-y-4 text-center">
					<div className="flex items-center justify-center gap-2">
						{BRAND_MENTION_PLATFORMS.map((platform) => (
							<div
								key={platform}
								className="flex h-10 w-10 items-center justify-center rounded-full bg-muted ring-1 ring-border">
								<PlatformIcon
									platform={platform}
									className="h-5 w-5"
									colored
								/>
							</div>
						))}
					</div>
					<div className="space-y-2">
						<p className="text-sm font-medium text-primary">
							{t('brandMentions.kicker')}
						</p>
						<h1 className="text-xl font-bold tracking-tight sm:text-2xl">
							{t('brandMentions.lockedHeadline')}
						</h1>
						<p className="text-sm text-muted-foreground sm:text-base">
							{isAddon
								? t('brandMentions.addonLockedDescription')
								: t('brandMentions.lockedDescription')}
						</p>
					</div>
					<ul className="mx-auto max-w-lg space-y-2 text-left">
						{benefits.map((benefit) => (
							<li key={benefit} className="flex items-start gap-2 text-sm">
								<CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
								<span>{benefit}</span>
							</li>
						))}
					</ul>
					<Button asChild size="lg" className="shadow-md">
						<Link to="/settings?tab=billing">
							<Sparkles className="h-4 w-4" />
							{isAddon
								? t('brandMentions.addonLockedCta')
								: t('brandMentions.lockedCta')}
						</Link>
					</Button>
				</div>
			</section>
		</div>
	)
}
